import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../utils/api';
import PLReconciliationGrid from './PLReconciliationGrid';
import ReconciliationPanel from './ReconciliationPanel';
import ReconciliationReviewPanel from './ReconciliationReviewPanel';
import PayrollAdjustmentForm from './PayrollAdjustmentForm';
import CarrierAdvanceForm from './CarrierAdvanceForm';
import MultiSourceReconciliation from './MultiSourceReconciliation';
import { CaseMatcherLink, CaseReconcileLink } from '../matcher/CaseMatcherLink';
import TaxReconciliation from '../wizard/TaxReconciliation';
import ReconciliationWorkbench from './ReconciliationWorkbench';
import {
  getPropertyReconciliation,
  type PropertyReconciliation,
} from '../../services/propertyReconciliationApi';
import { useCanonicalData } from '../wizard/v2/hooks';
import {
  ArrowLeft,
  LayoutGrid,
  GitBranch,
  Users,
  ArrowLeftRight,
  Loader2,
  AlertTriangle,
  ClipboardCheck,
  Download,
  Scale,
  HandCoins,
} from 'lucide-react';

type ViewMode = 'grid' | 'multi' | 'payroll' | 'carrier-advances' | 'merchant' | 'tax-reconciliation';

interface PLGridData {
  report_id: string;
  years: number[];
  pl_structure: any;
  pl_grid: any;
  reconciliation_status: Record<string, string>;
  pattern_event_id?: string | null;
  catch_all_items: Array<[string, string]>;
  data_source?: 'transactions' | 'pl_summary' | 'none';
}

interface SelectedCell {
  year: number;
  month: number;
  section: string;
  line_item: string;
}

const TABS: { key: ViewMode; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'grid',     label: 'P&L Grid',         icon: LayoutGrid    },
  { key: 'multi',    label: 'Source Comparison', icon: GitBranch     },
  { key: 'payroll',  label: 'Payroll Addbacks',  icon: Users         },
  { key: 'carrier-advances', label: 'Carrier Advances', icon: HandCoins },
  { key: 'merchant', label: 'Merchant Review',   icon: ArrowLeftRight },
  { key: 'tax-reconciliation', label: 'Tax Reconciliation', icon: Scale },
];

export interface StaffWorkbenchContentProps {
  reportId?: string;
  showPageHeader?: boolean;
}

export function StaffWorkbenchContent({ reportId: reportIdProp, showPageHeader = true }: StaffWorkbenchContentProps) {
  const { reportId: routeReportId } = useParams<{ reportId: string }>();
  const reportId = reportIdProp ?? routeReportId;
  const navigate = useNavigate();

  const [viewMode, setViewMode]         = useState<ViewMode>('grid');
  const [plGridData, setPlGridData]     = useState<PLGridData | null>(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);

  // Property line-item reconciliation (contractor-vs-carrier Xactimate match).
  // Read-only: GET /result only, no writes. Renders as a card alongside the P&L
  // grid when present; suppressed entirely on BI-only cases (404 → null).
  const [propertyRecon, setPropertyRecon] = useState<PropertyReconciliation | null>(null);
  const [propertyReconLoading, setPropertyReconLoading] = useState(false);

  // Tax Reconciliation tab data — reuses the canonical-data hook (report_id ≡ claim_id);
  // same source SidebarStep3 feeds TaxReconciliation. No new endpoint.
  const {
    canonicalData: taxCanonicalData,
    financialData: taxPlData,
    tax1065Files: taxAllFiles,
    isLoading: isTaxDataLoading,
  } = useCanonicalData(reportId ?? null);
  const form1065s = (taxAllFiles || []).filter((f: any) => f.form_type === '1065');
  const k1Forms = (taxAllFiles || []).filter((f: any) => f.form_type === 'K-1' || f.form_type === 'K1');

  useEffect(() => {
    if (!reportId) return;
    fetchPLGrid();
  }, [reportId]);

  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    setPropertyReconLoading(true);
    getPropertyReconciliation(reportId)
      .then((r) => { if (!cancelled) setPropertyRecon(r); })
      .catch(() => { if (!cancelled) setPropertyRecon(null); })
      .finally(() => { if (!cancelled) setPropertyReconLoading(false); });
    return () => { cancelled = true; };
  }, [reportId]);

  const fetchPLGrid = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.get(`/api/v1/reconciliation/pl-grid/${reportId}`);
      setPlGridData(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load P&L grid');
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (year: number, month: number, section: string, lineItem: string) => {
    if (!plGridData || plGridData.data_source !== 'transactions') {
      // Summarised data — no transaction-level drill-down
      return;
    }
    setSelectedCell({ year, month, section, line_item: lineItem });
  };

  const handleBackToGrid = () => setSelectedCell(null);

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto">

      {showPageHeader && (
        <>
          {/* Back + page header + secondary nav */}
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard/accountant')}
                className="cs-meta flex items-center gap-2 transition-colors"
                style={{ color: 'var(--color-text-muted)' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-text-secondary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; }}
                data-testid="workbench-back"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </button>
              <div className="w-px h-5" style={{ background: 'var(--color-border)' }} />
              <div>
                <h1 className="cs-h2">P&L Workbench</h1>
                {reportId && (
                  <p className="cs-figure mt-0.5" style={{ fontSize: '0.6875rem', color: 'var(--color-text-dim)' }}>{reportId}</p>
                )}
              </div>
            </div>

            {/* Engine Review + Export Center quick-jump */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate(`/dashboard/accountant/review/${reportId}`)}
                data-testid="workbench-engine-review-btn"
                className="cs-chip flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-colors shadow-sm"
                style={{ borderColor: 'var(--color-border)', background: 'var(--color-bg-surface)', color: 'var(--color-text-secondary)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-bg-surface)'; }}
              >
                <ClipboardCheck className="w-3.5 h-3.5" style={{ color: 'var(--color-brand)' }} />
                Engine Review
              </button>
              <button
                type="button"
                onClick={() => navigate(`/dashboard/accountant/export/${reportId}`)}
                data-testid="workbench-export-btn"
                className="cs-chip flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-colors shadow-sm"
                style={{ borderColor: 'var(--color-border)', background: 'var(--color-bg-surface)', color: 'var(--color-text-secondary)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-bg-surface)'; }}
              >
                <Download className="w-3.5 h-3.5" style={{ color: 'var(--color-brand)' }} />
                Export Center
              </button>
              {/* Item Matcher — the line-item matcher over this same reconciliation. Placed
                  next to recon here for owner/accountant; also on the adjuster/DS case nav
                  (matching is a walks-the-lines function, not accountant-only). */}
              <CaseMatcherLink reportId={reportId} />
              <CaseReconcileLink reportId={reportId} />
            </div>
          </div>
        </>
      )}

      {/* Tab bar */}
      <div
        role="tablist"
        aria-label="Workbench tools"
        className="flex gap-1 mb-6 p-1 rounded-lg w-fit"
        style={{ background: 'var(--color-bg-elevated)' }}
      >
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={viewMode === key}
            onClick={() => { setViewMode(key); setSelectedCell(null); }}
            data-testid={`workbench-tab-${key}`}
            className="cs-chip flex items-center gap-2 px-4 py-2 rounded-md transition-colors min-h-[36px]"
            style={
              viewMode === key
                ? { background: 'var(--color-bg-surface)', color: 'var(--color-needs-human-text)', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }
                : { color: 'var(--color-text-muted)', background: 'transparent' }
            }
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ── P&L Grid ── */}
      {viewMode === 'grid' && (
        selectedCell ? (
          /* Transaction-level drill-down */
          <ReconciliationPanel
            reportId={reportId!}
            year={selectedCell.year}
            month={selectedCell.month}
            section={selectedCell.section}
            lineItem={selectedCell.line_item}
            eventId={plGridData?.pattern_event_id ?? undefined}
            onBack={handleBackToGrid}
          />
        ) : loading ? (
          <div className="flex items-center justify-center py-24" data-testid="workbench-loading">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-brand)' }} />
          </div>
        ) : error ? (
          <div className="rounded-xl p-6 flex items-start gap-3" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
            <div>
              <p className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>Failed to load P&L grid</p>
              <p className="cs-body mt-1" style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
              <button
                type="button"
                onClick={fetchPLGrid}
                className="cs-body mt-3 underline hover:no-underline"
                style={{ color: 'var(--color-needs-human-text)' }}
              >
                Retry
              </button>
            </div>
          </div>
        ) : plGridData ? (
          <PLReconciliationGrid
            plGridData={plGridData}
            onCellClick={handleCellClick}
          />
        ) : null
      )}

      {/* ── Property line-item matching workbench (ticket 174, read-only; below the grid) ── */}
      {/* Replaces the Slice-1 summary card: the workbench folds its headline counts into
          its own status strip (one surface, one scroll — no two-count drift). Mount ONLY
          once a reconciliation is present (most cases are BI-only, 404 → null), so no empty
          card flashes on the reconcile view. Walk the building→unit→room hierarchy, read
          every carrier/expert pair side-by-side, and see the name-mismatch word-diff. */}
      {viewMode === 'grid' && !selectedCell && propertyRecon && (
        <div className="mt-6 max-w-[1120px]">
          <ReconciliationWorkbench reconciliation={propertyRecon} />
        </div>
      )}

      {/* ── Source Comparison ── */}
      {viewMode === 'multi' && reportId && (
        <MultiSourceReconciliation reportId={reportId} />
      )}

      {/* ── Payroll Addbacks ── */}
      {viewMode === 'payroll' && reportId && (
        <PayrollAdjustmentForm reportId={reportId} />
      )}

      {/* ── Carrier Advances ── */}
      {viewMode === 'carrier-advances' && reportId && (
        <CarrierAdvanceForm reportId={reportId} />
      )}

      {/* ── Tax Reconciliation (P&L ↔ 1065, clickable basis pairs) ── */}
      {viewMode === 'tax-reconciliation' && reportId && (
        isTaxDataLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-brand)' }} />
          </div>
        ) : (
          <TaxReconciliation
            plData={taxPlData || {}}
            tax1065Files={form1065s}
            k1Forms={k1Forms}
            varianceExplanations={taxCanonicalData?.variance_explanations}
          />
        )
      )}

      {/* ── Merchant Review ── */}
      {viewMode === 'merchant' && reportId && (
        <ReconciliationReviewPanel reportId={reportId} />
      )}

    </div>
  );
}

export default StaffWorkbenchContent;

